import { JIRA_FLOW_ORDER, jiraModuleColor, jiraStatusColor, jiraStatusKey, jiraTitleParts, type JiraIssueCard, type JiraIssueDetail, type JiraPerson, type JiraTransition } from '@novachat/shared';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { Link2, X } from 'lucide-react';
import { useEffect, useId, useRef, useState, type FormEvent, type ReactNode } from 'react';
import { create } from 'zustand';
import { client } from '../lib/client';
import { closeOnBackdrop } from '../lib/dismiss';
import { errorMessage } from '../lib/errors';
import { clock, dayLabel, fullDate } from '../lib/time';
import { AssignPanel, Person } from './JiraCards';
import { PriorityBars } from './JiraPage';
import { useJira } from './JiraSettings';
import { Markdown } from './Markdown';
import { useSettings } from './SettingsDialog';

/**
 * An issue's details (docs/plans/2026-11-04-jira-design.md §6, §7), opened from its card or from
 * "Benim işlerim"; rendered once by Shell. `card` shows until the details come (and is all there
 * is without Jira connected).
 */
export const useJiraIssue = create<{ open: { key: string; card: JiraIssueCard | null } | null; show(key: string, card?: JiraIssueCard | null): void; close(): void }>()(
  (set) => ({
    open: null,
    show: (key, card = null) => set({ open: { key, card } }),
    close: () => set({ open: null }),
  }),
);

export function JiraIssueDialog() {
  const open = useJiraIssue((s) => s.open);
  return open ? <IssueDialog key={open.key} issueKey={open.key} card={open.card} /> : null;
}

const when = (iso: string) => `${dayLabel(iso)} ${clock(iso)}`;
const SECTION = 'm-0 text-[11.5px] font-semibold tracking-[0.08em] text-fg-3 uppercase';

/** Where the person may move the issue: Jira's transitions for it, the current status among them, in workflow order. */
function statusOptions(current: JiraIssueCard['status'], transitions: readonly JiraTransition[]) {
  const options = new Map<string, { status: JiraIssueCard['status']; transitionId: string | null }>();
  options.set(current.name, { status: current, transitionId: null });
  for (const t of transitions) if (!options.has(t.to.name)) options.set(t.to.name, { status: t.to, transitionId: t.id });
  return [...options.values()].sort((a, b) => JIRA_FLOW_ORDER.indexOf(jiraStatusKey(a.status)) - JIRA_FLOW_ORDER.indexOf(jiraStatusKey(b.status)));
}

function StatusPill({ status }: { status: JiraIssueCard['status'] }) {
  const color = jiraStatusColor(status);
  return (
    <span style={{ backgroundColor: color.tint, color: color.dot }} className="flex h-6 flex-none items-center gap-1.5 rounded-full px-[9px] text-[12.5px] font-semibold whitespace-nowrap">
      <span aria-hidden="true" style={{ backgroundColor: color.dot }} className="size-1.5 rounded-full" />
      {status.name}
    </span>
  );
}

function IssueDialog({ issueKey, card }: { issueKey: string; card: JiraIssueCard | null }) {
  const dialog = useRef<HTMLDialogElement>(null);
  const body = useRef<HTMLDivElement>(null);
  const titleId = useId();
  useEffect(() => {
    dialog.current?.showModal();
    // Focus the content (arrow keys scroll it), not the first button in the header.
    body.current?.focus();
  }, []);
  const close = () => dialog.current?.close();
  const jira = useJira();
  const me = jira.data?.connection ?? null;
  const detail = useQuery({ queryKey: ['jira-issue', issueKey], queryFn: () => client.api.jiraIssue(issueKey), enabled: !!me, staleTime: 0, retry: 1 });
  const issue: JiraIssueCard | null = detail.data ?? card;
  const url = issue?.url ?? `${jira.data?.site ?? ''}/browse/${issueKey}`;
  const parts = issue ? jiraTitleParts(issue.summary, issue.epic ?? null) : null;
  const [copied, setCopied] = useState(false);

  async function copy() {
    try {
      await navigator.clipboard.writeText(url);
      setCopied(true);
      setTimeout(() => setCopied(false), 1800);
    } catch {
      // nothing to say: the button stays as it was
    }
  }

  return (
    <dialog
      ref={dialog}
      onClick={closeOnBackdrop}
      onClose={() => useJiraIssue.getState().close()}
      aria-labelledby={titleId}
      className="m-auto h-dvh max-h-none w-full max-w-none overflow-hidden border-0 bg-surface-2 p-0 text-fg-1 backdrop:bg-[rgba(5,6,8,0.62)] md:h-auto md:max-h-[calc(100dvh-96px)] md:w-[min(880px,calc(100vw-48px))] md:rounded-2xl md:border md:border-white/[0.08] md:shadow-[0_30px_80px_rgba(0,0,0,0.55)]"
    >
      <div className="flex h-full max-h-[inherit] flex-col">
        <header className="flex flex-none flex-wrap items-center gap-x-3 gap-y-2 border-b border-white/[0.07] px-4 pt-[max(12px,env(safe-area-inset-top))] pb-3 md:h-14 md:flex-nowrap md:py-0 md:pr-4 md:pl-6">
          <span className="font-mono text-[14px] font-semibold tracking-[-0.01em]">{issueKey}</span>
          {issue && <StatusPill status={issue.status} />}
          {parts?.ref && <span className="font-mono text-[12px] text-fg-3">↳ {parts.ref}</span>}
          <span className="flex-1" />
          <button type="button" onClick={() => void copy()} className="nc-press h-8 rounded-md border border-white/10 bg-transparent px-3 text-[13px] text-fg-2 hover:bg-white/[0.05] max-md:order-last">
            {copied ? 'Kopyalandı' : 'Bağlantıyı kopyala'}
          </button>
          <a href={url} target="_blank" rel="noopener noreferrer" className="nc-press flex h-8 items-center rounded-md bg-brand-500 px-3 text-[13px] font-bold text-[#1A1208] no-underline max-md:order-last">
            Jira’da aç ↗
          </a>
          <button type="button" aria-label="Kapat" onClick={close} className="nc-press grid size-8 place-items-center rounded-md border-0 bg-transparent text-fg-2 hover:bg-white/[0.06]">
            <X size={16} />
          </button>
        </header>

        <div className="grid min-h-0 flex-1 grid-cols-1 overflow-y-auto md:grid-cols-[minmax(0,1fr)_270px] md:overflow-hidden">
          <div ref={body} tabIndex={-1} className="flex flex-col gap-[22px] px-5 pt-6 pb-7 outline-none md:min-h-0 md:overflow-y-auto md:px-7">
            <div className="flex flex-col gap-2">
              {parts?.module && (
                <span style={{ color: jiraModuleColor(parts.module) }} className="text-[13px] font-medium">
                  {parts.module}
                </span>
              )}
              <h2 id={titleId} className="m-0 text-[24px] leading-[1.3] font-semibold tracking-[-0.015em] [overflow-wrap:anywhere]">
                {parts?.title ?? issueKey}
              </h2>
            </div>
            {/* Phones: status and fields come before the description. */}
            <div className="md:hidden">{issue && <Side compact issue={issue} detail={detail.data ?? null} me={me?.accountId ?? null} />}</div>
            {!me ? (
              <div className="rounded-xl border border-line bg-surface-1 p-4">
                <p className="m-0 text-[14px] text-fg-2">Açıklamayı ve yorumları görmek, buradan durum değiştirip yorum yazmak için Jira hesabını bağla.</p>
                {jira.data?.available && (
                  <button
                    type="button"
                    onClick={() => {
                      close();
                      useSettings.getState().show('connections');
                    }}
                    className="nc-press mt-3 flex h-9 items-center gap-2 rounded-md border-0 bg-brand-500 px-3.5 text-[13.5px] font-bold text-white"
                  >
                    <Link2 size={15} aria-hidden="true" />
                    Jira hesabını bağla
                  </button>
                )}
              </div>
            ) : detail.data ? (
              <Body issue={detail.data} />
            ) : (
              <p role={detail.isError ? 'alert' : undefined} className={`m-0 text-[14px] ${detail.isError ? 'text-[#EE9191]' : 'text-fg-3'}`}>
                {detail.isError ? errorMessage(detail.error) : 'Yükleniyor…'}
              </p>
            )}
            {me && detail.data && <CommentBox issueKey={issueKey} />}
          </div>
          <aside aria-label="Durum ve bilgiler" className="hidden border-l border-white/[0.07] bg-surface-1 p-5 md:block md:min-h-0 md:overflow-y-auto">
            {issue && <Side issue={issue} detail={detail.data ?? null} me={me?.accountId ?? null} />}
          </aside>
        </div>
      </div>
    </dialog>
  );
}

function Field({ label, children, compact }: { label: string; children: ReactNode; compact?: boolean }) {
  return compact ? (
    <div className="flex min-h-[46px] items-center justify-between gap-3 border-t border-white/[0.05] px-3.5 py-2 text-[14px] first:border-t-0">
      <span className="flex-none text-fg-3">{label}</span>
      <div className="flex min-w-0 flex-col items-end text-right">{children}</div>
    </div>
  ) : (
    <div className="flex flex-col gap-1.5">
      <span className="text-[12px] text-fg-3">{label}</span>
      <div className="text-[13.5px]">{children}</div>
    </div>
  );
}

function Who({ person, empty }: { person: Pick<JiraPerson, 'accountId' | 'name' | 'userId'> | null; empty: string }) {
  return person ? (
    <span className="flex min-w-0 items-center gap-2">
      <Person person={person} size={22} />
      <span className="truncate">{person.name}</span>
    </span>
  ) : (
    <span className="text-fg-3">{empty}</span>
  );
}

/** The right column: status to move to, then priority, assignee, reporter and sprint. */
function Side({ issue, detail, me, compact = false }: { issue: JiraIssueCard; detail: JiraIssueDetail | null; me: string | null; compact?: boolean }) {
  const queries = useQueryClient();
  const transitions = useQuery({ queryKey: ['jira-transitions', issue.key], queryFn: () => client.api.jiraTransitions(issue.key), enabled: !!me, staleTime: 0 });
  const [moving, setMoving] = useState<string | null>(null);
  const [assigning, setAssigning] = useState(false);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const changed = async () => {
    await Promise.all([
      queries.invalidateQueries({ queryKey: ['jira-issue', issue.key] }),
      queries.invalidateQueries({ queryKey: ['jira-transitions', issue.key] }),
      queries.invalidateQueries({ queryKey: ['jira-mine'] }),
    ]);
  };
  const options = statusOptions(issue.status, transitions.data ?? []);

  async function move(name: string, transitionId: string) {
    setMoving(name);
    setError(null);
    try {
      await client.api.jiraTransition(issue.key, transitionId);
      await changed();
    } catch (e) {
      setError(errorMessage(e));
    } finally {
      setMoving(null);
    }
  }

  async function assignToMe() {
    if (!me) return;
    setBusy(true);
    setError(null);
    try {
      await client.api.jiraAssign(issue.key, me);
      await changed();
    } catch (e) {
      setError(errorMessage(e));
    } finally {
      setBusy(false);
    }
  }

  const quiet = 'nc-press h-7 rounded-md border-0 bg-transparent px-1.5 text-[12.5px] font-semibold text-fg-3 hover:bg-white/[0.05] hover:text-fg-1 disabled:opacity-50';
  return (
    <div className="flex flex-col gap-5">
      <div role="radiogroup" aria-label="Durum" className={compact ? 'flex flex-wrap gap-1.5' : 'flex flex-col gap-1.5'}>
        <p className={`${SECTION} pb-1 ${compact ? 'w-full' : ''}`}>{compact ? 'Durumu değiştir' : 'Durum'}</p>
        {options.map(({ status, transitionId }) => {
          const on = status.name === issue.status.name;
          const color = jiraStatusColor(status);
          if (compact) {
            return (
              <button
                key={status.name}
                type="button"
                role="radio"
                aria-checked={on}
                disabled={!me || on || !transitionId || moving !== null}
                onClick={() => transitionId && void move(status.name, transitionId)}
                style={on ? { backgroundColor: color.tint, borderColor: color.dot } : undefined}
                className={`flex h-[34px] items-center gap-[7px] rounded-[10px] border px-3 text-[13.5px] font-medium disabled:cursor-default ${on ? 'text-fg-1' : 'border-white/[0.08] bg-transparent text-fg-2'} ${moving === status.name ? 'opacity-60' : ''}`}
              >
                <span aria-hidden="true" style={{ backgroundColor: color.dot }} className="size-[7px] rounded-full" />
                {status.name}
              </button>
            );
          }
          return (
            <button
              key={status.name}
              type="button"
              role="radio"
              aria-checked={on}
              disabled={!me || on || !transitionId || moving !== null}
              onClick={() => transitionId && void move(status.name, transitionId)}
              style={on ? { backgroundColor: color.tint } : undefined}
              className={`flex h-[34px] items-center gap-2.5 rounded-md border-0 px-2.5 text-left text-[13.5px] ${on ? 'text-fg-1' : 'bg-transparent text-fg-2 enabled:hover:bg-white/[0.04]'} disabled:cursor-default`}
            >
              <span aria-hidden="true" style={{ borderColor: on ? color.dot : '#3A3F49' }} className="grid size-3.5 flex-none place-items-center rounded-full border-[1.6px]">
                {(on || moving === status.name) && <span style={{ backgroundColor: color.dot }} className="size-1.5 rounded-full" />}
              </span>
              {status.name}
            </button>
          );
        })}
        {me && transitions.isPending && <p className="m-0 px-2.5 text-[12.5px] text-fg-3">Durumlar yükleniyor…</p>}
      </div>
      <div className={compact ? 'flex flex-col rounded-[14px] bg-surface-1' : 'flex flex-col gap-3.5 border-t border-white/[0.06] pt-[18px]'}>
        <Field compact={compact} label="Öncelik">
          <span className="flex items-center gap-[7px]">
            <PriorityBars name={issue.priority} />
            {issue.priority ?? '—'}
          </span>
        </Field>
        <Field compact={compact} label="Atanan">
          <Who person={issue.assignee} empty="Atanmamış" />
          {me && (
            <div className={compact ? '-mr-1.5 flex gap-1' : '-ml-1.5 mt-1 flex gap-1'}>
              <button type="button" aria-expanded={assigning} onClick={() => setAssigning((a) => !a)} className={quiet}>
                Değiştir
              </button>
              {issue.assignee?.accountId !== me && (
                <button type="button" disabled={busy} onClick={() => void assignToMe()} className={quiet}>
                  Bana ata
                </button>
              )}
            </div>
          )}
          {assigning && (
            <div className="mt-2 w-full rounded-md border border-line-strong bg-surface-2 p-1 text-left">
              <AssignPanel
                card={issue}
                done={() => {
                  setAssigning(false);
                  void changed();
                }}
              />
            </div>
          )}
        </Field>
        <Field compact={compact} label="Raporlayan">
          <Who person={detail?.reporter ?? null} empty={detail ? 'Bilinmiyor' : '—'} />
        </Field>
        <Field compact={compact} label="Sprint">
          <span>{detail?.sprint ?? '—'}</span>
        </Field>
        {detail && (
          <Field compact={compact} label="Güncellendi">
            <time dateTime={detail.updated} title={fullDate(detail.updated)} className="text-fg-2">
              {when(detail.updated)}
            </time>
          </Field>
        )}
      </div>
      {error && (
        <p role="alert" className="m-0 text-[12.5px] text-[#EE9191]">
          {error}
        </p>
      )}
    </div>
  );
}

function Body({ issue }: { issue: JiraIssueDetail }) {
  return (
    <>
      <section aria-label="Açıklama" className="flex flex-col gap-2.5">
        <h3 className={SECTION}>Açıklama</h3>
        {issue.description ? (
          <div className="text-[14.5px] leading-relaxed text-fg-body [overflow-wrap:anywhere]">
            <Markdown content={issue.description} everyone={false} />
          </div>
        ) : (
          <p className="m-0 text-[14px] text-fg-3">Açıklama yok.</p>
        )}
      </section>

      <section aria-label="Yorumlar" className="flex flex-col gap-3">
        <h3 className={SECTION}>Yorumlar · {issue.commentCount}</h3>
        {issue.commentCount > issue.comments.length && (
          <a href={issue.url} target="_blank" rel="noopener noreferrer" className="text-[13px] text-teal-400 underline-offset-2 hover:underline">
            Daha eski {issue.commentCount - issue.comments.length} yorum Jira’da
          </a>
        )}
        {issue.comments.length === 0 ? (
          <p className="m-0 text-[14px] text-fg-3">Henüz yorum yok.</p>
        ) : (
          <ol className="m-0 flex list-none flex-col gap-4 p-0">
            {issue.comments.map((comment) => (
              <li key={comment.id} className="flex gap-3">
                {comment.author ? <Person person={comment.author} size={28} /> : <span aria-hidden="true" className="size-7 flex-none rounded-full bg-surface-3" />}
                <div className="min-w-0 flex-1">
                  <p className="m-0 flex items-baseline gap-2">
                    <span className="truncate text-[14px] font-semibold text-fg-1">{comment.author?.name ?? 'Biri'}</span>
                    <time dateTime={comment.created} title={fullDate(comment.created)} className="flex-none text-[11.5px] text-fg-3">
                      {when(comment.created)}
                    </time>
                  </p>
                  <div className="text-[14.5px] leading-normal text-fg-body [overflow-wrap:anywhere]">
                    <Markdown content={comment.body} everyone={false} />
                  </div>
                </div>
              </li>
            ))}
          </ol>
        )}
      </section>
    </>
  );
}

function CommentBox({ issueKey }: { issueKey: string }) {
  const queries = useQueryClient();
  const [text, setText] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  async function send(event?: FormEvent) {
    event?.preventDefault();
    if (!text.trim() || busy) return;
    setBusy(true);
    setError(null);
    try {
      await client.api.jiraComment(issueKey, text);
      setText('');
      await queries.invalidateQueries({ queryKey: ['jira-issue', issueKey] });
    } catch (e) {
      setError(errorMessage(e));
    } finally {
      setBusy(false);
    }
  }

  return (
    <form onSubmit={(e) => void send(e)} className="flex flex-col gap-2">
      {error && (
        <p role="alert" className="m-0 text-[12.5px] text-[#EE9191]">
          {error}
        </p>
      )}
      <div className="flex items-end gap-2">
        <textarea
          rows={1}
          value={text}
          onChange={(e) => setText(e.target.value)}
          onKeyDown={(e) => {
            if (e.key === 'Enter' && (e.metaKey || e.ctrlKey)) void send();
          }}
          aria-label={`${issueKey} için Jira yorumu`}
          placeholder="Jira’ya yorum yaz"
          title="Yorum Jira’ya senin adınla gider"
          className="max-h-40 min-h-[42px] flex-1 resize-none rounded-md border border-line-strong bg-surface-1 px-3 py-2 text-base text-fg-1 outline-none [field-sizing:content] placeholder:text-fg-3 focus:border-brand-400 sm:text-sm"
        />
        <button type="submit" disabled={busy || !text.trim()} className="nc-press h-[42px] flex-none rounded-md border-0 bg-brand-500 px-4 text-[13.5px] font-bold text-white disabled:opacity-50">
          Gönder
        </button>
      </div>
    </form>
  );
}
